// Deterministic scenario simulator for the New Citizen sandbox.
// Scenarios produce city geometry, entity waypoint tracks and a timeline;
// the blueprint graph (from sdkStore) is executed against the timeline to
// produce node-execution events (spawn SWAT, broadcasts, barricades...).

export const DURATION = 150; // seconds

const lerp = (a, b, t) => a + (b - a) * t;

export function posAt(track, t) {
  if (!track.length) return [0, 0];
  if (t <= track[0].t) return [track[0].x, track[0].z];
  for (let i = 0; i < track.length - 1; i++) {
    const a = track[i], b = track[i + 1];
    if (t >= a.t && t <= b.t) {
      const p = (t - a.t) / Math.max(0.001, b.t - a.t);
      return [lerp(a.x, b.x, p), lerp(a.z, b.z, p)];
    }
  }
  const last = track[track.length - 1];
  return [last.x, last.z];
}

export function headingAt(track, t) {
  if (track.length < 2) return 0;
  for (let i = 0; i < track.length - 1; i++) {
    const a = track[i], b = track[i + 1];
    if (t >= a.t && t <= b.t) return Math.atan2(b.x - a.x, b.z - a.z);
  }
  const a = track[track.length - 2], b = track[track.length - 1];
  return Math.atan2(b.x - a.x, b.z - a.z);
}

// ---------- scenario builders ----------

function activeShooter() {
  const buildings = [
    { x: 0, z: 0, w: 60, d: 40, h: 10, label: "Northgate Mall" },
    { x: -70, z: -40, w: 30, d: 26, h: 14 },
    { x: 70, z: -40, w: 30, d: 26, h: 12 },
    { x: -70, z: 45, w: 34, d: 24, h: 9 },
    { x: 70, z: 45, w: 34, d: 24, h: 11 },
  ];
  const zones = [
    { id: "staging-a", label: "SWAT Staging A", x: -45, z: 70, r: 12, color: "#2E6BFF" },
    { id: "cordon", label: "Outer Cordon", x: 0, z: 0, r: 55, color: "#FF6B1A" },
    { id: "exit-s", label: "South Exit", x: 0, z: 28, r: 8, color: "#22c55e" },
  ];
  const entities = [];
  // civilians scatter from the mall after shots (t=15)
  for (let i = 0; i < 12; i++) {
    const cx = -20 + (i % 6) * 8, cz = -10 + Math.floor(i / 6) * 14;
    entities.push({
      id: `civ-${i}`, kind: "civilian", fov: 0,
      track: [
        { t: 0, x: cx, z: cz },
        { t: 15, x: cx + Math.sin(i) * 3, z: cz + Math.cos(i) * 3 },
        { t: 45, x: (cx > 0 ? 1 : -1) * (30 + i * 3), z: 60 + (i % 4) * 6 },
        { t: 80, x: (cx > 0 ? 1 : -1) * 80, z: 90 },
      ],
    });
  }
  entities.push({
    id: "suspect-1", kind: "suspect", fov: 70,
    track: [{ t: 0, x: -15, z: 5 }, { t: 20, x: 10, z: -5 }, { t: 45, x: 20, z: 10 }, { t: 90, x: 0, z: 0 }, { t: 150, x: -5, z: -10 }],
  });
  entities.push({
    id: "player", kind: "player", fov: 90,
    track: [{ t: 0, x: -45, z: 70 }, { t: 25, x: -20, z: 35 }, { t: 60, x: 0, z: 26 }, { t: 120, x: 5, z: 5 }, { t: 150, x: 0, z: 0 }],
  });
  for (let i = 0; i < 2; i++) {
    entities.push({
      id: `police-${i}`, kind: "police", fov: 80,
      track: [{ t: 0, x: -60 + i * 10, z: 75 }, { t: 18, x: -30 + i * 8, z: 40 }, { t: 40, x: -8 + i * 16, z: 24 }, { t: 100, x: -4 + i * 8, z: 12 }],
    });
  }
  entities.push({ id: "veh-patrol-1", kind: "vehicle", fov: 0, track: [{ t: 0, x: -55, z: 78 }, { t: 14, x: -35, z: 52 }, { t: 150, x: -35, z: 52 }] });
  const shotsAt = 15;
  return { id: "active_shooter", label: "Active Shooter — Northgate Mall", buildings, zones, entities, shotsAt, staging: zones[0], suspectMood: "hostile" };
}

function barricade() {
  const buildings = [
    { x: 0, z: 0, w: 26, d: 20, h: 6, label: "14 Reseda Ave" },
    { x: -55, z: -30, w: 30, d: 24, h: 7 },
    { x: 55, z: -30, w: 30, d: 24, h: 7 },
    { x: -55, z: 40, w: 30, d: 22, h: 6 },
    { x: 55, z: 40, w: 30, d: 22, h: 8 },
  ];
  const zones = [
    { id: "staging-a", label: "SWAT Staging A", x: -40, z: 70, r: 10, color: "#2E6BFF" },
    { id: "perimeter", label: "Containment Perimeter", x: 0, z: 0, r: 34, color: "#FF6B1A" },
    { id: "negotiation", label: "Negotiation Range", x: 0, z: 14, r: 10, color: "#a78bfa" },
  ];
  const entities = [
    { id: "suspect-1", kind: "suspect", fov: 65, track: [{ t: 0, x: 4, z: 2 }, { t: 30, x: -5, z: 3 }, { t: 70, x: 5, z: -4 }, { t: 150, x: 0, z: 0 }] },
    { id: "hostage-1", kind: "hostage", fov: 0, track: [{ t: 0, x: -3, z: -3 }, { t: 150, x: -3, z: -3 }] },
    { id: "player", kind: "player", fov: 90, track: [{ t: 0, x: -40, z: 70 }, { t: 30, x: -15, z: 30 }, { t: 70, x: 0, z: 16 }, { t: 150, x: 0, z: 14 }] },
  ];
  // perimeter units hold the cordon
  [[-26, 18], [26, 18], [-24, -16], [24, -16]].forEach(([x, z], i) => {
    entities.push({ id: `police-${i}`, kind: "police", fov: 80, track: [{ t: 0, x: x * 2.4, z: z * 3.6 }, { t: 25, x, z }, { t: 150, x, z }] });
  });
  entities.push({ id: "veh-patrol-1", kind: "vehicle", fov: 0, track: [{ t: 0, x: -50, z: 75 }, { t: 18, x: -30, z: 30 }, { t: 150, x: -30, z: 30 }] });
  entities.push({ id: "veh-patrol-2", kind: "vehicle", fov: 0, track: [{ t: 0, x: -54, z: 75 }, { t: 22, x: 30, z: 30 }, { t: 150, x: 30, z: 30 }] });
  return { id: "barricade", label: "High-Risk Barricade — 14 Reseda Ave", buildings, zones, entities, shotsAt: 0, staging: zones[0], suspectMood: "agitated" };
}

export function getScenario(id) {
  return id === "barricade" ? barricade() : activeShooter();
}

// ---------- blueprint execution ----------

// Walk the graph: trigger -> (logic chain) -> actions. Returns timeline events.
export function executeBlueprint(nodes, edges, scenario) {
  const events = [];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const outOf = (id, handle) => edges.filter((e) => e.source === id && (handle ? e.sourceHandle === handle : true)).map((e) => byId[e.target]).filter(Boolean);

  const triggerTime = (d) => {
    if (d.kind === "call_911") return 4;
    if (d.kind === "shots_fired") return scenario.shotsAt || 0;
    if (d.kind === "time_elapsed") return Math.min(DURATION - 5, d.seconds ?? 60);
    if (d.kind === "area_arrival") return 32;
    return 0;
  };

  const walkLogic = (node) => {
    // returns list of outgoing action nodes following deterministic branch rules
    if (!node) return [];
    if (node.type === "action") return [node];
    if (node.type === "profile") return outOf(node.id).flatMap(walkLogic);
    if (node.type === "logic") {
      const d = node.data || {};
      let pass = true;
      if (d.kind === "escalation_chance") pass = (d.chance ?? 50) >= 50;
      else if (d.kind === "mood_check") pass = (d.mood || "hostile") === scenario.suspectMood;
      else if (d.kind === "sightline_check") pass = true;
      const next = outOf(node.id, pass ? "yes" : "no");
      const branch = next.length ? next : outOf(node.id);
      return branch.flatMap(walkLogic);
    }
    return [];
  };

  const spawned = { swat: 0 };
  nodes.filter((n) => n.type === "trigger").forEach((trig) => {
    const t = triggerTime(trig.data || {});
    events.push({ t, nodeId: trig.id, kind: "trigger", label: `TRIGGER // ${(trig.data?.label || trig.data?.kind || "trigger").toUpperCase()}` });
    const logicChain = outOf(trig.id);
    logicChain.forEach((next) => {
      if (next.type === "logic") events.push({ t: t + 1, nodeId: next.id, kind: "logic", label: `CHECK // ${(next.data?.label || "logic").toUpperCase()}` });
    });
    const actions = logicChain.flatMap(walkLogic);
    const uniq = [...new Map(actions.map((a) => [a.id, a])).values()];
    uniq.forEach((act, i) => {
      const d = act.data || {};
      const at = Math.min(DURATION - 2, t + 3 + i * 4);
      if (d.kind === "spawn_swat") {
        const n = d.units ?? 4;
        for (let s = 0; s < n; s++) {
          scenario.entities.push({
            id: `swat-${spawned.swat++}`, kind: "swat", fov: 85, spawnAt: at,
            track: [
              { t: 0, x: scenario.staging.x, z: scenario.staging.z },
              { t: at, x: scenario.staging.x + (s % 3) * 3 - 3, z: scenario.staging.z + Math.floor(s / 3) * 3 },
              { t: at + 18, x: (scenario.zones[1]?.x ?? 0) + (s % 3) * 6 - 6, z: (scenario.zones[1]?.z ?? 0) + 20 + Math.floor(s / 3) * 5 },
              { t: DURATION, x: (s % 3) * 6 - 6, z: 14 + Math.floor(s / 3) * 5 },
            ],
          });
        }
        events.push({ t: at, nodeId: act.id, kind: "action", label: `ACTION // SPAWN SWAT x${n} @ ${d.staging || "staging"}` });
      } else if (d.kind === "radio_broadcast") {
        events.push({ t: at, nodeId: act.id, kind: "radio", label: `RADIO // ${d.message || "broadcast"}` });
      } else if (d.kind === "trigger_barricade") {
        scenario.barriers = scenario.barriers || [];
        [[-14, 24], [14, 24]].forEach(([x, z], bi) => scenario.barriers.push({ id: `bar-${events.length}-${bi}`, x, z, at }));
        events.push({ t: at, nodeId: act.id, kind: "action", label: `ACTION // BARRICADE @ ${d.location || "perimeter"}` });
      } else if (d.kind === "set_hostage_state") {
        events.push({ t: at, nodeId: act.id, kind: "action", label: `ACTION // HOSTAGE STATE -> ${(d.state || "captive").toUpperCase()}`, hostageState: d.state });
      }
    });
  });

  events.sort((a, b) => a.t - b.t);
  return events;
}

export function buildSimulation(scenarioId, nodes, edges) {
  const scenario = getScenario(scenarioId);
  const events = executeBlueprint(nodes, edges, scenario);
  return { ...scenario, events, duration: DURATION };
}
